import React, { useState } from "react";
import styled, { keyframes } from "styled-components";
import API from "../../../util/api";
import { useSelector } from "react-redux";
import Report2 from "../../../assets/image/report2.png"
import { showErrorModal } from "../../../util/general";

// Spinner animation
const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

// Full-screen loading overlay
const LoadingOverlay = styled.div`
  position: fixed;
  top: 0; left: 0;
  width: 100%; height: 100%;
  background: rgba(0,0,0,0.5);
  z-index: 9999;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

// Spinner circle
const Spinner = styled.div`
  border: 4px solid #f3f3f3;
  border-top: 4px solid ${({ colors }) => colors.buttonBackground};
  border-radius: 50%;
  width: 36px; height: 36px;
  animation: ${spin} 1s linear infinite;
`;

// Text under spinner
const OverlayText = styled.div`
  margin-top: 1em;
  color: white;
  font-size: 1.1em;
`;

const Container = styled.div.attrs({ id: "bigbox" })`
  background: #f8f8f8;
  padding: 1.5em;
`;

const Heading = styled.h1`
  margin: 0 0 0.5em;
  color: ${({ colors }) => colors.labelText};
`;

const SectionsLayout = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  align-items: flex-start;
`;

const Section = styled.div`
  font-family: "Source Sans Pro", sans-serif;
  font-size: 13px;
  text-align: center;
  flex: 1 1 ${({ width }) => `${width}px`};
  min-width: 200px;
  max-width: ${({ width }) => `${width}px`};
  margin-top: 10px;
`;

const Card3D = styled.div`
  perspective: 800px;
  margin: 20px auto 0;
  width: 160px;
  & > div {
    background: white;
    border-radius: 5px;
    transform: rotateX(6deg) rotateY(-4deg);
    transform-origin: bottom center;
    box-shadow: 6px 12px 20px rgba(0, 0, 0, 0.15);
    transition: transform 0.2s ease, box-shadow 0.2s ease;
    &:hover {
      transform: rotateX(0deg) rotateY(0deg) translateY(-5px);
      box-shadow: 8px 16px 24px rgba(0, 0, 0, 0.2);
    }
  }
`;

const CardInner = styled.div` padding: 10px; `;

const ReportImage = styled.img`
  display: block;
  width: 150px; height: 194px;
  margin: 0 auto; border-radius: 3px;
  cursor: pointer;
`;

const Links = styled.div` margin-top: 8px; `;

const PDFLink = styled.a`
  font-family: "Source Sans Pro", sans-serif;
  font-size: 13px;
  color: #2c4960;
  text-decoration: none;
  margin: 0 8px;
  cursor: pointer;
  &:hover { text-decoration: underline; }
`;

export default function ERateCertificationReports({ colors }) {
  const [loading, setLoading] = useState(false);
  const { invoice_id: invoiceId } =
    useSelector((s) => s.auth.selected_subscriptions) || {};

  // Helper to fetch & download a blob (pdf or csv)
  const downloadAndSave = async (apiFn, defaultName) => {
    setLoading(true);
    try {
      const { blob, filename } = await apiFn({ invoice_id: invoiceId });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = defaultName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Download failed:", err);
      showErrorModal("Unable to download report: " + (err.message || ""));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading && (
        <LoadingOverlay>
          <Spinner colors={colors} />
          <OverlayText>Requesting report… please wait</OverlayText>
        </LoadingOverlay>
      )}

      <Container colors={colors}>
        <Heading colors={colors}>E-Rate Certification Reports</Heading>
        <br/>

        <SectionsLayout>
          <Section width={240}>
            Educator E-Rate Certification Report
            <Card3D>
              <CardInner>
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    downloadAndSave(
                      API.downloadERATECertReport,
                      `EducatorERateCertReport_${invoiceId}.pdf`
                    );
                  }}
                >
                  <ReportImage
                    src={Report2}
                    alt="Educator E-rate Certification Report"
                  />
                </a>
              </CardInner>
            </Card3D>
            <Links>
              <PDFLink
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  downloadAndSave(
                    API.downloadERATECertReport,
                    `EducatorERateCertReport_${invoiceId}.pdf`
                  );
                }}
              >
                pdf
              </PDFLink>
              <PDFLink
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  downloadAndSave(
                    API.downloadERATECertReportCSV,
                    `EducatorERateCertReport_${invoiceId}.csv`
                  );
                }}
              >
                csv
              </PDFLink>
            </Links>
          </Section>

          <Section width={240}>
            Pending E-Rate Certification Report
            <Card3D>
              <CardInner>
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    downloadAndSave(
                      API.downloadPendingERATECertReport,
                      `PendingERateCertReport_${invoiceId}.pdf`
                    );
                  }}
                >
                  <ReportImage
                    src={Report2}
                    alt="Pending E-rate Certification Report"
                  />
                </a>
              </CardInner>
            </Card3D>
            <Links>
              <PDFLink
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  downloadAndSave(
                    API.downloadPendingERATECertReport,
                    `PendingERateCertReport_${invoiceId}.pdf`
                  );
                }}
              >
                pdf
              </PDFLink>
              <PDFLink
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  downloadAndSave(
                    API.downloadPendingERATECertReportCSV,
                    `PendingERateCertReport_${invoiceId}.csv`
                  );
                }}
              >
                csv
              </PDFLink>
            </Links>
          </Section>
        </SectionsLayout>
      </Container>
    </>
  );
}
